import type { DemoTools } from "../identity/demo";
import type { SessionLimits } from "../identity/logic/session-clock";
import type { SessionPort } from "./session-port";
import { createMockIdentity } from "../identity/mock";
import { browserPersistence } from "../identity/mock/persistence";
import { IDENTITY_STORAGE_KEY } from "../identity/mock/state";
import type { IdentityApi, StaffApi } from "../identity/types";
import type { OrthosApi } from "../types";
import { createMockApi } from "./backend";
import { createProjectStore } from "./project-store";

/** The whole in-browser backend: projects (OrthosApi), identity, the staff console and demo tools, wired together. */
export interface MockBackend {
  api: OrthosApi;
  identity: IdentityApi;
  staff: StaffApi;
  demo: DemoTools;
  /** See MockIdentity.startDevSession (`?dev-session=asha`). */
  startDevSession(key: string): boolean;
}

export interface MockBackendOptions {
  latencyMs: number;
  limits: SessionLimits;
  demoOrg: { name: string; projectPrefix: string };
  /** The session the projects mock follows; the mock identity's own unless live identity supplies one. */
  session?: SessionPort | undefined;
}

export async function createMockBackend({
  latencyMs,
  limits,
  demoOrg,
  session,
}: MockBackendOptions): Promise<MockBackend> {
  const projects = createProjectStore(Date.now());
  const identity = await createMockIdentity({
    latencyMs,
    limits,
    demoOrg,
    persistence: browserPersistence(IDENTITY_STORAGE_KEY),
    random: crypto,
    now: () => Date.now(),
    userAgent: navigator.userAgent,
    projects: { find: (projectId) => projects.locate(projectId) },
  });
  return {
    api: createMockApi({ latencyMs, session: session ?? identity.port, projects }),
    identity: identity.identity,
    staff: identity.staff,
    demo: identity.demo,
    startDevSession: identity.startDevSession,
  };
}
